"use client";

import { useCallback, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { AssetInfo, HoldingChange } from "../types";
import {
  mapCikChangeTypeToHolding,
  formatNumberWithSuffix2DecimalsSigned,
} from "../utils";
import {
  getPositionSymbol,
  getPositionLogo,
  getPositionName,
  enrichAssetFromStock,
  fetchStocksById,
} from "./cikPositionStockEnrich";
import { getMemberApi } from "@/src/services/helper";
import type { CikAssetPosition } from "@features/api/memberApi";
import type { Stock } from "@features/api/openData";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";

/**
 * 将 CikAssetPosition + Stock 转为 HoldingChange（asset 名称/logo 与列表表一致）
 */
function mapPositionToChange(
  pos: CikAssetPosition,
  stock?: Stock,
): HoldingChange {
  const symbol = getPositionSymbol(pos);
  const logo = getPositionLogo(pos);
  const name = getPositionName(pos);
  const changeType = mapCikChangeTypeToHolding(pos.change_type);

  const baseAsset: AssetInfo = {
    id: pos.asset_id ?? "",
    symbol,
    name,
    logo,
  };

  // 后端会把 change_amount 调整为绝对值，这里统一按绝对值处理，符号由 changeType 决定
  const rawShares = Math.abs(pos.change_amount ?? 0);

  return {
    asset: enrichAssetFromStock(baseAsset, stock),
    // 原始股数（绝对值），展示用 changeSharesDisplay + changeType 决定符号
    changeShares: rawShares,
    changeSharesDisplay: formatNumberWithSuffix2DecimalsSigned(
      rawShares,
      changeType,
    ),
    changeType,
  };
}

export interface UseHoldingChangeTableParams {
  /** CIK ID */
  cikId: string;
  /** 是否启用请求 */
  enabled?: boolean;
}

/**
 * 持仓变动表格 Hook
 * - 第一页展示 5 条数据，点击 "View Full History" 后每次加载 10 条
 * - 与 useHoldingListTable 一致：用 stocks.getStocks 补充资产名称、logo
 */
export function useHoldingChangeTable({
  cikId,
  enabled = true,
}: UseHoldingChangeTableParams) {
  const { openDataApi } = useOspApiClientProvider();

  const infiniteQuery = useInfiniteQuery({
    queryKey: ["cikPositions", cikId, "changes"],
    queryFn: async ({ pageParam }) => {
      const api = await getMemberApi();
      if (!api?.ciks?.getCikPositions) {
        return { data: [], next_token: undefined };
      }
      const res = await api.ciks.getCikPositions(cikId, {
        limit: pageParam === undefined ? 5 : 10,
        sort: "CHANGE",
        next_token: pageParam as string | undefined,
        // @ts-expect-error yaml 类型定义错误
        with_asset: false,
      });
      const positions = res?.data?.data ?? [];
      const assetIds = positions
        .map((p) => p.asset_id)
        .filter((id): id is string => Boolean(id));
      const stocksById = await fetchStocksById(openDataApi, assetIds, {
        withTradingData: false,
        withRating: false,
      });

      const data = positions.map((pos) =>
        mapPositionToChange(
          pos,
          pos.asset_id ? stocksById[pos.asset_id] : undefined,
        ),
      );

      return {
        data,
        next_token: res?.data?.next_token,
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_token,
    enabled: enabled && Boolean(cikId) && Boolean(openDataApi),
    staleTime: 1000 * 60 * 5,
  });

  const hasLoadedOnce = (infiniteQuery.data?.pages?.length ?? 0) > 0;

  const changes: HoldingChange[] = useMemo(() => {
    const allPages = infiniteQuery.data?.pages ?? [];
    const list: HoldingChange[] = [];
    for (const page of allPages) {
      list.push(...(page.data ?? []));
    }
    return list;
  }, [infiniteQuery.data?.pages]);

  const hasMore = Boolean(infiniteQuery.hasNextPage);
  /** 仅首屏加载中（无数据时表格显示 loading） */
  const isLoading = infiniteQuery.isLoading;
  /** 正在加载下一页（点 more 时只在按钮上体现，不盖住整表） */
  const isLoadingMore = infiniteQuery.isFetchingNextPage;

  const handleLoadMore = useCallback(() => {
    infiniteQuery.fetchNextPage();
  }, [infiniteQuery]);

  return {
    changes,
    isLoading,
    hasLoadedOnce,
    isLoadingMore,
    hasMore,
    handleLoadMore,
  };
}
